import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { Navigate, useLocation } from 'react-router-dom'
import { ROUTES } from '@/routes/routes'

interface Props {
  children: React.ReactNode
}

// 401 handling (MFA_REQUIRED -> verify-2fa, any other 401 -> sign-in) lives in api/client.ts's
// response interceptor, not here — it needs to run for every request through the `api` axios
// instance, not just while this component happens to be mounted.
export function ProtectedRoute({ children }: Props) {
  const { isLoaded, isSignedIn } = useAuth()
  const location = useLocation()

  // Landing here fresh right after a sign-in (e.g. the SSO callback's setActive() completing
  // and navigating straight to a protected route) can render one tick with isLoaded=true but
  // isSignedIn still reporting its pre-sign-in value, before Clerk's store finishes propagating
  // the update — redirecting to Sign In on that single stale render flashes it, even though the
  // session is already valid a moment later. Give isSignedIn one tick to settle before treating
  // a "not signed in" reading as authoritative.
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    if (!isLoaded) return
    const id = setTimeout(() => setSettled(true), 0)
    return () => clearTimeout(id)
  }, [isLoaded])

  if (!isLoaded || !settled) {
    return null
  }

  if (!isSignedIn) {
    // pathname + search, not pathname alone — a route with its own query params (e.g.
    // /app/accept-invite?token=...) would otherwise lose them across the sign-in detour.
    const currentPath = location.pathname + location.search
    return (
      <Navigate
        to={`${ROUTES.AUTH.SIGN_IN}?redirectTo=${encodeURIComponent(currentPath)}`}
        replace
      />
    )
  }

  return <>{children}</>
}
